import * as THREE from 'three';
import { between, fract, pulse, type CritterWorld, type Random } from './critter-math.js';
import { bakeCritter, parts } from './critter-parts.js';

const TAU = Math.PI * 2;
/** A shore crab is a hand's width; it is drawn at this multiple so it reads from the isometric camera. */
export const CRAB_SCALE = 2.4;
/** Metres at which a crab notices something, and its sideways speed walking and running (m/s). */
const SCARE_REACH = 4.2, WALK_SPEED = .45, RUN_SPEED = 1.7;

export interface CrabAction {
  /** 0–1 scuttling and the legs' place in their cycle (whole cycles). */
  walk: number; stride: number;
  /** 0–1 a raised claw waving, and 0–1 sunk into the sand. */
  wave: number; burrow: number;
  idle: number;
}
export const restingCrab = (): CrabAction => ({ walk: 0, stride: 0, wave: 0, burrow: 0, idle: 0 });

/** A low-poly shore crab facing +Z: a domed shell, eyes on stalks, two claws and six jointed legs, walking sideways. */
export class CrabModel {
  readonly root = new THREE.Group();
  private body = new THREE.Group();
  private claws: THREE.Group[] = [];
  private legs: { group: THREE.Group; side: number; slot: number }[] = [];
  private eyes: THREE.Group[] = [];

  constructor(private seed = 0, tone = 0) {
    const shell = [0xb8523a, 0xa8634a, 0xc2704b][tone % 3], pale = 0xd9a07a, { body } = this;
    this.root.add(body);
    parts.blob(body, shell, [.11, .042, .08], [0, .06, 0]);
    parts.blob(body, pale, [.07, .02, .05], [0, .035, 0]);
    for (const side of [-1, 1]) {
      const eye = new THREE.Group(); eye.position.set(side * .04, .085, .07); body.add(eye);
      parts.stick(eye, shell, .005, .035).position.y = .015; parts.blob(eye, 0x15110f, [.012, .012, .012], [0, .04, .004]);
      this.eyes.push(eye);
      // Claw: an arm out to the side that bends forward, ending in two pincers.
      const claw = new THREE.Group(); claw.position.set(side * .1, .06, .06); body.add(claw);
      const arm = parts.stick(claw, shell, .011, .09); arm.rotation.z = Math.PI / 2; arm.position.x = side * .04;
      const hand = new THREE.Group(); hand.position.set(side * .09, 0, .02); claw.add(hand);
      parts.blob(hand, shell, [.034, .022, .04], [0, 0, .03]);
      for (const jaw of [-1, 1]) parts.blob(hand, pale, [.011, .012, .03], [jaw * .014, 0, .065]);
      this.claws.push(claw);
      for (let slot = 0; slot < 3; slot++) {
        const leg = new THREE.Group(); leg.position.set(side * .1, .05, .04 - slot * .04); body.add(leg);
        const upper = parts.stick(leg, shell, .006, .07); upper.rotation.z = side * -1.1; upper.position.set(side * .03, -.005, 0);
        const lower = parts.stick(leg, shell, .005, .06); lower.rotation.z = side * .3; lower.position.set(side * .07, -.045, 0);
        this.legs.push({ group: leg, side, slot });
      }
    }
    bakeCritter(this.root);
  }

  apply(a: CrabAction) {
    const t = a.idle + this.seed;
    this.body.position.y = .03 - a.burrow * .1; this.body.scale.setScalar(1 - a.burrow * .35);
    this.legs.forEach(({ group, side, slot }) => {
      // Legs on the two sides alternate, and each pair works out of step with its neighbours: the crab's tripod-like scuttle.
      const cycle = a.stride + slot * .33 + (side > 0 ? .5 : 0);
      group.rotation.set(Math.sin(cycle * TAU) * .35 * a.walk, 0, side * Math.max(0, Math.cos(cycle * TAU)) * .35 * a.walk);
    });
    this.claws.forEach((claw, index) => {
      // Held up and open when alarmed or scuttling, and one waves now and then when idle.
      const wave = index ? a.wave * Math.sin(t * 14) * .3 : 0;
      claw.rotation.set(-.2 - .25 * a.walk + wave, (index ? -1 : 1) * -.15, (index ? 1 : -1) * (.1 + a.wave * .5));
    });
    this.eyes.forEach((eye, index) => { eye.rotation.set(Math.sin(t * 1.7 + index) * .15, 0, (index ? 1 : -1) * (.1 + Math.sin(t * 2.3) * .08)); });
  }
}

/**
 * A crab's wits. It sits on the sand with its eyes up, waves a claw now and then and sidles a short way; if something comes
 * near it runs sideways away from it and sinks into the sand, gone.
 */
export class CrabBrain {
  readonly action: CrabAction = restingCrab();
  mode: 'idle' | 'scuttle' | 'flee' | 'burrow' = 'idle';
  /** Set once it has sunk into the sand; the field then removes it. */
  gone = false;
  private timer: number; private side = 1; private fleeTime = 0;

  constructor(public x: number, public z: number, public heading: number, private random: Random) {
    this.action.idle = random() * 40; this.timer = between(random, 1, 4);
  }

  update(dt: number, world: CritterWorld) {
    const a = this.action;
    a.idle += dt;
    const threat = this.mode === 'burrow' ? null : world.threat(this.x, this.z, SCARE_REACH);
    if (threat && this.mode !== 'flee') {
      this.mode = 'flee'; this.fleeTime = 0;
      // Run along the shore, whichever way takes it further from the threat.
      const across = this.heading + Math.PI / 2, dot = Math.sin(across) * (this.x - threat.x) + Math.cos(across) * (this.z - threat.z);
      this.side = dot >= 0 ? 1 : -1;
    }
    const target = this.mode === 'idle' ? 0 : 1;
    a.walk += (target - a.walk) * (1 - Math.exp(-dt * 8));
    a.wave += ((threat || this.mode === 'flee' ? 1 : pulse(fract(a.idle / 9), .5, .12)) - a.wave) * (1 - Math.exp(-dt * 6));
    if (this.mode === 'burrow') {
      a.burrow = Math.min(1, a.burrow + dt * 2.2); a.walk *= Math.exp(-dt * 10);
      if (a.burrow >= 1) this.gone = true;
      return;
    }
    if (this.mode === 'idle') {
      this.timer -= dt;
      if (this.timer <= 0) { this.mode = 'scuttle'; this.side = this.random() < .5 ? -1 : 1; this.timer = between(this.random, .8, 2.2); }
      return;
    }
    const speed = this.mode === 'flee' ? RUN_SPEED : WALK_SPEED, direction = this.heading + this.side * Math.PI / 2, step = speed * a.walk * dt;
    const nx = this.x + Math.sin(direction) * step, nz = this.z + Math.cos(direction) * step;
    if (world.free(nx + Math.sin(direction) * .25, nz + Math.cos(direction) * .25, .2)) { this.x = nx; this.z = nz; a.stride += step / .09; }
    else this.side = -this.side;
    this.timer -= dt; this.fleeTime += dt;
    if (this.mode === 'flee' && this.fleeTime > 1.6) { this.mode = 'burrow'; return; }
    if (this.mode === 'scuttle' && this.timer <= 0) { this.mode = 'idle'; this.timer = between(this.random, 2, 7); }
  }

}
